import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, banner as bannerApi, type BannerAppearance, type BannerTemplateIssue } from '../../../lib/api';
import { checkTemplate } from '../../../lib/template-rules';
import { templateEdited } from './defaults';

type Template = BannerAppearance['template'];

/**
 * The template rules on the current draft: the local port runs at once on
 * every change; the server's reading follows 300 ms after the last keystroke
 * while the editor is open, and anything it adds is appended. A built-in
 * design with no edits has nothing to check.
 */
export function useTemplateCheck(template: Template, withServer: boolean): { issues: BannerTemplateIssue[]; checking: boolean } {
  const { html, css } = template;
  const edited = templateEdited(template);
  const local = useMemo(() => (edited ? checkTemplate(html, css) : []), [edited, html, css]);
  const [server, setServer] = useState<BannerTemplateIssue[]>([]);
  const [checking, setChecking] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!withServer || !edited) { setServer([]); setChecking(false); return; }
    const id = ++seq.current;
    setChecking(true);
    const t = setTimeout(() => {
      bannerApi.checkTemplate(html, css)
        .then((r) => { if (seq.current === id) setServer(Array.isArray(r.issues) ? r.issues : []); })
        .catch((err: unknown) => {
          // The route may not be there yet; the local rules stand on their own.
          if (seq.current === id && !(err instanceof ApiError && err.status === 404)) setServer([]);
        })
        .finally(() => { if (seq.current === id) setChecking(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [withServer, edited, html, css]);

  const issues = useMemo(() => {
    const seen = new Set(local.map((i) => i.code));
    return [...local, ...server.filter((i) => !seen.has(i.code))];
  }, [local, server]);

  return { issues, checking };
}
